/**
 * Release Board Page
 *
 * Kanban-style board for the release flow with drag-and-drop.
 * Columns: In review, Changes requested, Released, Scheduled.
 * Every column is cursor paged on its own (useInfiniteQuery) and shares the
 * URL-persisted filters; header totals come from the counts endpoint.
 */

import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { TFunction } from 'i18next';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InteractiveSafeMouseSensor, InteractiveSafeTouchSensor } from '@/utils/dndSensors';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { VideoPlayBadge } from '@/components/ui/VideoPlayBadge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { RetryImage } from '@/components/ui';
import { TextPostTile } from '@/components/ui/TextPostTile';
import { Tooltip } from '@/components/ui/Tooltip';
import { focusSurface, groupFocusSurface } from '@/components/ui/focusStyles';
import { HoverCard } from '@/components/ui/HoverCard';
import { PageHeader } from '@/components/layout/PageHeader';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useApprovePost, useSubmitForApproval, useSubmitVariationForApproval } from '@/hooks/useApproval';
import { BoardCardMenu } from '@/components/release/board/BoardCardMenu';
import { useWithdrawFromReview } from '@/hooks/useWithdrawFromReview';
import { useReleaseBoard, useReleaseBoardGate, type BoardColumnState, type ReleaseBoardAvailability } from '@/hooks/useReleaseBoard';
import { useReleaseBoardFilters } from '@/hooks/useReleaseBoardFilters';
import { BoardFilterBar } from '@/components/release/board/BoardFilterBar';
import { NEEDED_BY_CHIP_CLASS, NEEDED_BY_TEXT_CLASS, getNeededByInfo } from '@/components/release/neededBy';
import { teamQueryKeys } from '@/lib/queryKeys';
import { EMPTY_BOARD_FILTERS, parseBoardColumnParam, type BoardItem, type ReleaseBoardColumnKey } from '@/types/releaseBoard';
import { ScheduleFromApprovalModal } from '@/components/approval';
import { announceRelease } from '@/stores/releaseUndo.store';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { openReleaseDrawer, useReleaseDrawerStore, type ReleaseDrawerTab } from '@/stores/releaseDrawer.store';
import { ReleaseTour } from '@/components/release/ReleaseTour';
import { PendingApproval, PostStatus } from '@/types/approval';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { approvalService } from '@/services/approval.service';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { CLIENT_CHECKLIST_ITEMS } from '@/services/onboarding.service';

/**
 * Centralized approval status color theme — brand palette colors only.
 * Uses Tailwind config color names (no hardcoded hex) for whitelabel compatibility.
 * Change colors here to update columns, popovers, and drawer in one place.
 */
const APPROVAL_STATUS_THEME = {
  PENDING: {
    // Column header
    headerBg: 'bg-peach',
    bodyBg: 'bg-peach/10',
    borderClass: 'border-b-peach',
    headerText: 'text-gray-900',
    // Gradient (popover + drawer): airy brand style, white → soft color
    gradientFrom: 'from-white',
    gradientTo: 'to-peach',
    // Badge pill on gradient
    badgeBg: 'bg-white/60',
    badgeText: 'text-gray-800',
  },
  CHANGES_REQUESTED: {
    headerBg: 'bg-soft-coral',
    bodyBg: 'bg-soft-coral/10',
    borderClass: 'border-b-coral-200',
    headerText: 'text-gray-900',
    gradientFrom: 'from-white',
    gradientTo: 'to-soft-coral',
    badgeBg: 'bg-white/60',
    badgeText: 'text-coral-700',
  },
  APPROVED: {
    headerBg: 'bg-soft-mint',
    bodyBg: 'bg-soft-mint/15',
    borderClass: 'border-b-primary-300',
    headerText: 'text-gray-900',
    gradientFrom: 'from-white',
    gradientTo: 'to-soft-mint',
    badgeBg: 'bg-white/60',
    badgeText: 'text-primary-700',
  },
  SCHEDULED: {
    headerBg: 'bg-light-teal-gray/75',
    bodyBg: 'bg-light-teal-gray/15',
    borderClass: 'border-b-sage-gray',
    headerText: 'text-gray-900',
    gradientFrom: 'from-white',
    gradientTo: 'to-light-teal-gray',
    badgeBg: 'bg-white/60',
    badgeText: 'text-gray-800',
  },
} as const;

/**
 * Board column configuration
 */
interface BoardColumn {
  /** Droppable id */
  id: string;
  /** Backend column key */
  key: ReleaseBoardColumnKey;
  title: string;
  emptyText: string;
  color: string;
  bgColor: string;
  bodyBg: string; // Subtle column body tint for visual separation
  borderClass: string; // Tailwind border-b color class
  allowDrop: boolean; // Whether cards can be dropped here
}

// Note: Function to create columns - will be called inside component to access t()
const createBoardColumns = (t: TFunction<'common'>): BoardColumn[] => [
  {
    id: 'pending',
    key: 'in_review',
    title: t('release_status_in_review'),
    emptyText: t('release_board_empty_in_review'),
    color: APPROVAL_STATUS_THEME.PENDING.headerText,
    bgColor: APPROVAL_STATUS_THEME.PENDING.headerBg,
    bodyBg: APPROVAL_STATUS_THEME.PENDING.bodyBg,
    borderClass: APPROVAL_STATUS_THEME.PENDING.borderClass,
    allowDrop: true, // Allow resubmit from Changes Requested
  },
  {
    id: 'changes',
    key: 'changes_requested',
    title: t('release_status_changes_requested'),
    emptyText: t('release_board_empty_changes_requested'),
    color: APPROVAL_STATUS_THEME.CHANGES_REQUESTED.headerText,
    bgColor: APPROVAL_STATUS_THEME.CHANGES_REQUESTED.headerBg,
    bodyBg: APPROVAL_STATUS_THEME.CHANGES_REQUESTED.bodyBg,
    borderClass: APPROVAL_STATUS_THEME.CHANGES_REQUESTED.borderClass,
    allowDrop: true, // Can request changes
  },
  {
    id: 'approved',
    key: 'released',
    title: t('release_status_released'),
    emptyText: t('release_board_empty_released'),
    color: APPROVAL_STATUS_THEME.APPROVED.headerText,
    bgColor: APPROVAL_STATUS_THEME.APPROVED.headerBg,
    bodyBg: APPROVAL_STATUS_THEME.APPROVED.bodyBg,
    borderClass: APPROVAL_STATUS_THEME.APPROVED.borderClass,
    allowDrop: true, // Can approve/release
  },
  {
    id: 'scheduled',
    key: 'scheduled',
    title: t('release_status_scheduled'),
    emptyText: t('release_board_empty_scheduled'),
    color: APPROVAL_STATUS_THEME.SCHEDULED.headerText,
    bgColor: APPROVAL_STATUS_THEME.SCHEDULED.headerBg,
    bodyBg: APPROVAL_STATUS_THEME.SCHEDULED.bodyBg,
    borderClass: APPROVAL_STATUS_THEME.SCHEDULED.borderClass,
    allowDrop: true, // Drag APPROVED cards here to schedule them
  },
];

/**
 * Pending drop action
 */
interface PendingDropAction {
  item: BoardItem;
  /** Only the two confirmed drops: re-send (in_review) and release (released) */
  targetColumn: 'in_review' | 'released';
}

/**
 * Get approval status styling config
 */
function getApprovalStatusConfig(
  status: PostStatus,
  t: (key: string) => string
): {
  color: string;
  bgColor: string;
  label: string;
  icon: string;
  gradientFrom: string;
  gradientTo: string;
} {
  const theme = APPROVAL_STATUS_THEME[status as keyof typeof APPROVAL_STATUS_THEME];

  switch (status) {
    case 'PENDING':
      return {
        color: theme?.badgeText || 'text-gray-700',
        bgColor: theme?.badgeBg || 'bg-gray-100',
        label: t('release_status_in_review'),
        icon: ICONS.REVIEW,
        gradientFrom: theme?.gradientFrom || 'from-gray-100',
        gradientTo: theme?.gradientTo || 'to-gray-200',
      };
    case 'CHANGES_REQUESTED':
      return {
        color: theme?.badgeText || 'text-gray-700',
        bgColor: theme?.badgeBg || 'bg-gray-100',
        label: t('release_status_changes_requested'),
        icon: ICONS.CAUTION,
        gradientFrom: theme?.gradientFrom || 'from-gray-100',
        gradientTo: theme?.gradientTo || 'to-gray-200',
      };
    case 'APPROVED':
      return {
        color: theme?.badgeText || 'text-gray-700',
        bgColor: theme?.badgeBg || 'bg-gray-100',
        label: t('release_status_released'),
        icon: ICONS.RELEASE_STAMP,
        gradientFrom: theme?.gradientFrom || 'from-gray-100',
        gradientTo: theme?.gradientTo || 'to-gray-200',
      };
    case 'SCHEDULED':
      return {
        color: theme?.badgeText || 'text-gray-700',
        bgColor: theme?.badgeBg || 'bg-gray-100',
        label: t('release_status_scheduled'),
        icon: ICONS.CALENDAR,
        gradientFrom: theme?.gradientFrom || 'from-gray-100',
        gradientTo: theme?.gradientTo || 'to-gray-200',
      };
    case 'PUBLISHED':
      return {
        color: theme?.badgeText || 'text-gray-700',
        bgColor: theme?.badgeBg || 'bg-gray-100',
        label: t('release_status_published'),
        icon: ICONS.CHECK_CIRCLE,
        gradientFrom: theme?.gradientFrom || 'from-gray-100',
        gradientTo: theme?.gradientTo || 'to-gray-200',
      };
    default:
      return {
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
        label: status,
        icon: ICONS.INFO,
        gradientFrom: 'from-gray-100',
        gradientTo: 'to-gray-200',
      };
  }
}

interface PopoverDateRow {
  key: string;
  label: string;
  value: string;
  hint?: string;
  hintClass?: string;
}

function pushDateRow(rows: PopoverDateRow[], key: string, label: string, value: string, hint?: string, hintClass?: string): void {
  rows.push({ key, label, value, hint, hintClass });
}

/**
 * HoverCard popover content for approval cards
 */
const ApprovalCardPopover = ({ item }: { item: BoardItem }) => {
  const { t } = useTranslation('common');
  const { formatDateTime, timezone } = useTimezone();
  const { approval, post, canApprove, variation } = item;
  const mediaUrls = (variation?.mediaUrls?.length ? variation.mediaUrls : post.mediaUrls);
  const statusConfig = getApprovalStatusConfig(post.status, t);
  // The person who sent THIS item for review (a variation has its own sender),
  // not the parent post's author.
  const requesterName = approval.requestedBy?.name || post.author.name;

  // Date line is labelled by what it is.
  let dateLabel: string | null = null;
  if (post.status === 'PENDING' || post.status === 'CHANGES_REQUESTED') {
    dateLabel = t('release_popover_sent_for_review', { when: formatDateTime(approval.requestedAt) });
  } else if (post.status === 'APPROVED' && approval.reviewedAt) {
    dateLabel = t('release_popover_released_on', { when: formatDateTime(approval.reviewedAt) });
  }

  // Planned / next / released dates, one labelled row each (hidden when empty).
  const dateRows: PopoverDateRow[] = [];
  if (item.column === 'in_review' || item.column === 'changes_requested') {
    const needed = getNeededByInfo(item.neededBy, timezone, t);
    if (needed && item.neededBy) {
      pushDateRow(dateRows, 'needed', t('release_filters_needed_by'), formatDateTime(item.neededBy), needed.relative, NEEDED_BY_TEXT_CLASS[needed.tone]);
    }
  }
  if (item.column === 'scheduled' && item.nextOccurrenceAt) {
    pushDateRow(dateRows, 'next', t('release_popover_next_publish'), formatDateTime(item.nextOccurrenceAt));
  }
  if (item.column === 'released' && item.releasedAt) {
    pushDateRow(dateRows, 'released', t('release_status_released'), formatDateTime(item.releasedAt));
  }

  return (
    <div>
      {/* Gradient header */}
      <div className={cn('bg-gradient-to-r px-4 py-3', statusConfig.gradientFrom, statusConfig.gradientTo)}>
        <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium', statusConfig.bgColor, statusConfig.color)}>
          <Icon name={statusConfig.icon} size="xs" ariaHidden />
          {statusConfig.label}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex gap-3 mb-3">
          {/* Thumbnail */}
          {mediaUrls.length > 0 && (
            <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 shadow-sm ring-1 ring-gray-100">
              <RetryImage
                src={mediaUrls[0]}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {/* Caption */}
          <p className="text-sm text-gray-700 line-clamp-3 max-h-24 overflow-y-auto">
            {post.content || t('approval_no_content')}
          </p>
        </div>

        {/* Variation badge */}
        {variation && (
          <div className="flex items-center gap-1.5 mb-3 px-2 py-1 bg-secondary-50 border border-secondary-200 rounded-md w-fit">
            <Icon name={ICONS.LANGUAGE} size="xs" color="text-gray-700" ariaHidden />
            <span className="text-xs font-medium text-gray-800">{variation.title}</span>
          </div>
        )}

        {/* Requester + labelled date */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-primary-400 flex items-center justify-center text-xs font-semibold text-primary-400-contrast">
            {requesterName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-900 truncate">{requesterName}</p>
            {dateLabel && <p className="text-[10px] text-gray-500">{dateLabel}</p>}
          </div>
        </div>

        {dateRows.length > 0 && (
          <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {dateRows.map((r) => (
              <Fragment key={r.key}>
                <dt className="text-gray-500">{r.label}</dt>
                <dd className="text-gray-900 min-w-0">
                  {r.value}
                  {r.hint && <span className={cn('ml-1.5', r.hintClass ?? 'text-gray-600')}>({r.hint})</span>}
                </dd>
              </Fragment>
            ))}
          </dl>
        )}

        {/* Only where it is actionable: item in review and the viewer can release it */}
        {canApprove && post.status === 'PENDING' && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {t('release_popover_can_release')}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100">
        <span className="text-[10px] text-gray-400">
          {t('approval_click_for_details')}
        </span>
      </div>
    </div>
  );
};

/**
 * Detect if a media URL is a video by extension.
 * Used as a fallback when explicit mediaTypes are not present in the API response.
 */
function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0];
  return /\.(mp4|mov|webm|avi|mkv|m4v)$/.test(lower);
}

/** Compact change cue; hover explains it on desktop and tap explains it on touch devices. */
const ChangedIndicator = ({ label }: { label: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <Tooltip content={label} disabled={open} side="bottom">
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label={label}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className={cn('group inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md', focusSurface)}
          >
            <span className={cn('inline-flex h-5 w-5 items-center justify-center rounded bg-primary-700 text-primary-contrast shadow-sm', groupFocusSurface)}>
              <Icon name="compare" size="custom" className="text-[12px]" ariaHidden />
            </span>
          </button>
        </PopoverPrimitive.Trigger>
      </Tooltip>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={5}
          className="z-tooltip max-w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-lg"
        >
          {label}
          <PopoverPrimitive.Arrow className="fill-gray-900" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

/**
 * Approval card for the board — grid-style aspect-square card
 */
const ApprovalCard = ({
  item,
  onClick,
  isDragging = false,
  onWithdraw,
}: {
  item: BoardItem;
  onClick: () => void;
  isDragging?: boolean;
  /** In review + allowed to take it back: opens the confirm dialog (card menu) */
  onWithdraw?: (item: BoardItem) => void;
}) => {
  const { t } = useTranslation('common');
  const { timezone, formatDateOnly, formatTimeOnly, formatRelativeDate } = useTimezone();
  const { post, variation } = item;
  const mediaUrls = (variation?.mediaUrls?.length ? variation.mediaUrls : post.mediaUrls);

  // Detect if first media item is a video (via explicit type or URL extension)
  const firstMediaIsVideo =
    (post.mediaTypes?.[0] === 'VIDEO') ||
    (mediaUrls.length > 0 && isVideoUrl(mediaUrls[0]));

  // Carousel count: extra items beyond the first
  const totalMedia = post.mediaCount ?? mediaUrls.length;
  const extraCount = totalMedia > 1 ? totalMedia - 1 : 0;

  // "Needed by" only matters while the item is still being worked on.
  const needed =
    item.column === 'in_review' || item.column === 'changes_requested'
      ? getNeededByInfo(item.neededBy, timezone, t)
      : null;

  // Footer strip: next occurrence (Scheduled) or when it was released (Released).
  let footer: { lines: string[]; title: string } | null = null;
  if (item.column === 'scheduled' && item.nextOccurrenceAt) {
    const day = formatDateOnly(item.nextOccurrenceAt);
    const time = formatTimeOnly(item.nextOccurrenceAt);
    footer = {
      lines: [day, time],
      title: t('release_card_next_when', { when: `${day}, ${time}` }),
    };
  } else if (item.column === 'released' && item.releasedAt) {
    const text = t('release_card_released_when', { when: formatRelativeDate(item.releasedAt) });
    footer = { lines: [text], title: text };
  }

  return (
    <div
      className={cn(
        'group relative aspect-square rounded-xl overflow-hidden',
        'transition-all duration-300 ease-out',
        'bg-gray-100 cursor-pointer',
        isDragging
          ? 'opacity-50 scale-90'
          : 'hover:scale-[1.03] hover:shadow-xl hover:z-10',
        variation && 'ring-2 ring-secondary-300'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    >
      {/* Image or fallback */}
      {mediaUrls.length > 0 ? (
        <RetryImage
          src={mediaUrls[0]}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <TextPostTile text={post.content || variation?.title} emptyLabel={t('approval_no_content')} />
      )}

      {/* Video play indicator — centered overlay */}
      {firstMediaIsVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <VideoPlayBadge />
        </div>
      )}

      {/* Carousel count badge — top-right corner */}
      {extraCount > 0 && (
        <div className="absolute top-1.5 right-1.5">
          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-black/50 text-white leading-none backdrop-blur-sm">
            +{extraCount}
          </span>
        </div>
      )}

      {/* Top-left: needed-by chip + language-version badge */}
      {(needed || variation || item.hasChangesSinceLastSend) && (
        <div className="absolute top-1.5 left-1.5 flex max-w-[75%] flex-wrap items-center gap-1">
          {needed && (
            <span
              title={needed.label}
              className={cn(
                'px-1.5 py-0.5 text-[10px] font-semibold rounded leading-none shadow-sm',
                NEEDED_BY_CHIP_CLASS[needed.tone]
              )}
            >
              <span aria-hidden="true">{needed.short}</span>
              <span className="sr-only">{needed.label}</span>
            </span>
          )}
          {variation && (
            <Tooltip content={variation.title}>
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded flex items-center gap-0.5 shadow-sm bg-secondary-100 text-gray-800">
                <Icon name={ICONS.LANGUAGE} size="xs" ariaHidden />
              </span>
            </Tooltip>
          )}
          {item.hasChangesSinceLastSend && (
            <ChangedIndicator label={t('release_diff_title')} />
          )}
        </div>
      )}

      {/* Card menu: only In review cards the viewer may take back out of review */}
      {onWithdraw && item.column === 'in_review' && item.permissions?.canWithdraw === true && !isDragging && (
        <BoardCardMenu onWithdraw={() => onWithdraw(item)} />
      )}

      {/* Footer strip: next occurrence / released when */}
      {footer && (
        <div
          title={footer.title}
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-1.5 pb-1 pt-4 text-[10px] font-medium leading-tight text-white pointer-events-none"
        >
          {footer.lines.map((line) => (
            <span key={line} className="block line-clamp-2 break-words">
              {line}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Draggable approval card wrapper with HoverCard popover
 */
const DraggableCard = ({
  item,
  onClick,
  onWithdraw,
}: {
  item: BoardItem;
  onClick: () => void;
  onWithdraw: (item: BoardItem) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cardElement = (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn('touch-none', isDragging && 'cursor-grabbing')}
    >
      <ApprovalCard item={item} onClick={onClick} isDragging={isDragging} onWithdraw={onWithdraw} />
    </div>
  );

  return (
    <HoverCard
      trigger={cardElement}
      side="left"
      align="start"
      sideOffset={8}
      open={isDragging ? false : undefined}
      allowTriggerPointerEvents
    >
      <ApprovalCardPopover item={item} />
    </HoverCard>
  );
};

/**
 * Empty column state
 */
const EmptyColumnState = ({ text }: { text: string }) => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
      <Icon name={ICONS.CHECK_CIRCLE} size="md" color="text-gray-400" ariaHidden />
    </div>
    <p className="text-sm text-gray-600">{text}</p>
  </div>
);

/**
 * Shown instead of the columns where no board can exist (no brand picked, carte blanche ON,
 * standalone brand). Queries are disabled there, so nothing may render as loading.
 */
const BoardUnavailable = ({
  availability,
  canOpenPortfolio,
}: {
  availability: Exclude<ReleaseBoardAvailability, 'active' | 'loading'>;
  canOpenPortfolio: boolean;
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const copy = {
    no_brand: {
      title: t('release_board_no_brand_title'),
      desc: canOpenPortfolio ? t('release_board_no_brand_desc') : t('release_board_no_brand_desc_plain'),
    },
    workflow_off: { title: t('release_board_workflow_off_title'), desc: t('release_board_workflow_off_desc') },
    standalone: { title: t('release_board_standalone_title'), desc: t('release_board_standalone_desc') },
  }[availability];

  return (
    <div className="rounded-lg bg-white border border-gray-200 text-center py-16 px-6" role="status">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
        <Icon name={ICONS.CHECK_CIRCLE} size="lg" color="text-gray-500" ariaHidden />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{copy.title}</h2>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">{copy.desc}</p>
      {availability === 'no_brand' && canOpenPortfolio && (
        <Button variant="primary" onClick={() => navigate('/agency/portfolio')}>
          {t('release_board_open_portfolio')}
        </Button>
      )}
    </div>
  );
};

/** Shimmer blocks while a column loads its first page. */
const ColumnSkeleton = () => (
  <div
    aria-hidden="true"
  >
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="w-full aspect-square bg-gray-200/80 rounded-xl animate-pulse"
        />
      ))}
    </div>
  </div>
);

/** Placeholder for BoardFilterBar while availability loads: same wrapper and h-8 row, so the columns do not jump. */
const BoardFilterBarSkeleton = () => (
  <div className="space-y-2" aria-hidden="true">
    <div className="flex flex-wrap items-center gap-2">
      <div className="h-8 min-w-[160px] flex-1 animate-pulse rounded-full bg-gray-200/80 lg:w-64 lg:max-w-xs lg:flex-none" />
      <div className="hidden lg:flex items-center gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-gray-200/80" />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Whole-board placeholder while availability loads: the real column shells (same container, widths, header
 * height and body padding as BoardColumnComponent) so nothing shifts when the board appears. Each column keeps
 * its own ColumnSkeleton for later page loading.
 */
const BoardSkeleton = ({ columns }: { columns: BoardColumn[] }) => (
  <div
    aria-hidden="true"
    className="flex flex-col lg:flex-row gap-3 lg:gap-4 lg:overflow-x-auto pb-4 lg:min-h-[600px]"
  >
    {columns.map((column) => (
      <div
        key={column.id}
        className="flex flex-col lg:h-full lg:min-w-[300px] lg:flex-1 rounded-lg overflow-hidden"
      >
        <div
          className={cn(
            'flex items-center justify-between gap-2 px-4 py-2 lg:py-3 rounded-t-lg border-b-2',
            column.bgColor,
            column.borderClass
          )}
        >
          <span className="h-4 w-24 animate-pulse rounded bg-gray-300/70" />
          <span className="inline-flex h-6 min-w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/80 px-1">
            <span className="inline-block h-3 w-4 animate-pulse rounded bg-gray-300/70" />
          </span>
        </div>
        <div className={cn('flex-1 rounded-b-lg p-3', column.bodyBg)}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-full aspect-square bg-gray-200/80 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/** DOM ids a `?column=` deep link scrolls to / focuses (column box, then its heading). */
const boardColumnDomId = (key: ReleaseBoardColumnKey): string => `release-column-${key}`;
const boardColumnHeadingDomId = (key: ReleaseBoardColumnKey): string => `release-column-heading-${key}`;
/** How long the deep-linked column stays highlighted */
const DEEP_LINK_HIGHLIGHT_MS = 2500;

/**
 * Droppable board column component
 */
const BoardColumnComponent = ({
  column,
  state,
  total,
  filtersActive,
  onCardClick,
  onWithdraw,
  isOver,
  isDeepLinked,
}: {
  column: BoardColumn;
  state: BoardColumnState;
  /** Total from the counts endpoint (falls back to the column's own total) */
  total: number | null;
  filtersActive: boolean;
  onCardClick: (item: BoardItem) => void;
  onWithdraw: (item: BoardItem) => void;
  isOver?: boolean;
  /** Arrived here through a `?column=` link: highlighted for a moment (same ring as a drag-over) */
  isDeepLinked?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { setNodeRef } = useSortable({ id: column.id });

  const { items } = state;
  const shownTotal = total ?? state.total;

  // Scheduled column gets a special drop-zone visual when dragging over it
  const isScheduledColumn = column.id === 'scheduled';
  const showDropZone = isOver && column.allowDrop && isScheduledColumn;

  return (
    <div
      ref={setNodeRef}
      id={boardColumnDomId(column.key)}
      data-tour={column.key === 'in_review' ? 'release-board' : undefined}
      className={cn(
        'flex flex-col lg:h-full lg:min-w-[300px] lg:flex-1 rounded-lg overflow-hidden scroll-mt-20 lg:scroll-mt-0',
        'transition-all duration-200',
        isDeepLinked && 'ring-2 ring-primary-300 ring-offset-2',
        // Generic column: ring highlight
        isOver && column.allowDrop && !isScheduledColumn && 'ring-2 ring-primary-300 ring-offset-2',
        // Scheduled column: stronger ring when dragging over
        showDropZone && 'ring-2 ring-primary-500 ring-offset-2'
      )}
    >
      {/* Column header — rounded-t-lg unconditionally (mobile + desktop) */}
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-4 py-2 lg:py-3 rounded-t-lg border-b-2',
          column.bgColor,
          column.borderClass
        )}
      >
        <h3
          id={boardColumnHeadingDomId(column.key)}
          tabIndex={-1}
          className={cn('text-sm font-semibold tracking-wide truncate focus:outline-none', column.color)}
        >
          {column.title}
        </h3>
        <span
          className={cn(
            'inline-flex h-[24px] min-w-[24px] flex-shrink-0 items-center justify-center rounded-full px-1 text-[11px] font-medium leading-none tabular-nums',
            'bg-white/80 backdrop-blur-sm',
            column.color
          )}
        >
          {shownTotal !== null
            ? <span>{shownTotal}</span>
            : <span className="inline-block h-3 w-4 animate-pulse rounded bg-gray-300/70 align-middle" aria-hidden="true" />}
        </span>
      </div>

      {/* Cards container — mobile: horizontal scroll row, desktop: grid */}
      <div
        className={cn(
          'relative flex-1 rounded-b-lg p-3 lg:overflow-y-auto lg:max-h-none',
          column.bodyBg,
          'transition-colors duration-150',
          isOver && column.allowDrop && !isScheduledColumn && 'bg-primary-50/30',
          showDropZone && 'bg-primary-50/60'
        )}
      >
        {/* Scheduled column drop overlay — shown when dragging over it */}
        {showDropZone && (
          <div className="absolute inset-2 rounded-xl border-2 border-dashed border-primary-400 bg-primary-50/60 flex flex-col items-center justify-center gap-2 pointer-events-none z-10">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <Icon name={ICONS.CALENDAR} size="md" color="text-primary-600" ariaHidden />
            </div>
            <span className="text-sm font-semibold text-primary-700">
              {t('approval_drop_to_schedule')}
            </span>
          </div>
        )}

        {state.isLoading ? (
          <ColumnSkeleton />
        ) : state.isError ? (
          <div className="py-10 text-center" role="alert">
            <p className="mb-3 text-sm text-gray-600">{t('release_board_column_failed')}</p>
            <Button variant="secondary" size="sm" onClick={state.refetch}>
              {t('approval_try_again')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <EmptyColumnState
            text={filtersActive ? t('release_board_empty_filtered') : column.emptyText}
          />
        ) : (
          <>
            <div className="pb-3 lg:pb-0">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {items.map((item) => (
                  <div key={item.id} className="min-w-0 w-full">
                    <DraggableCard
                      item={item}
                      onClick={() => onCardClick(item)}
                      onWithdraw={onWithdraw}
                    />
                  </div>
                ))}
              </div>
            </div>

            {state.hasNextPage && (
              <button
                type="button"
                onClick={state.fetchNextPage}
                disabled={state.isFetchingNextPage}
                aria-busy={state.isFetchingNextPage}
                className={cn(
                  'mt-3 flex w-full items-center justify-center gap-2',
                  'px-3 py-2.5 rounded-lg text-sm font-semibold',
                  'bg-white border-2 border-dashed border-primary-300 text-primary-700',
                  'hover:bg-primary-50 hover:border-primary-500 hover:border-solid',
                  'disabled:cursor-wait disabled:opacity-70',
                  'transition-all duration-150 outline-none focus-visible:border-primary-600 focus-visible:border-solid'
                )}
              >
                {state.isFetchingNextPage ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-300 border-t-primary-600" aria-hidden="true" />
                ) : (
                  <Icon name={ICONS.ANGLE_DOWN} size="sm" ariaHidden />
                )}
                {t('release_board_load_more')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const parseDrawerTab = (raw: string | null): ReleaseDrawerTab | undefined =>
  raw === 'details' || raw === 'comments' || raw === 'history' ? raw : undefined;

export const ApprovalBoard = () => {
  const { t, i18n } = useTranslation('common');
  const postPermissions = usePostPermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDropAction | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePendingItem, setSchedulePendingItem] = useState<BoardItem | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  const { filters, activeCount, setFilters, clearAll } = useReleaseBoardFilters();
  const board = useReleaseBoard(filters);
  const { availability } = useReleaseBoardGate();
  const approveMutation = useApprovePost();
  const withdrawAction = useWithdrawFromReview();
  const [withdrawTarget, setWithdrawTarget] = useState<BoardItem | null>(null);
  const resubmitMutation = useSubmitForApproval();
  const resubmitVariationMutation = useSubmitVariationForApproval();
  const { progress, completeChecklistItem } = useOnboarding();
  const { user } = useAuth();
  const { isAgencyStaff, isInGodMode, teamId: effectiveTeamId } = useEffectiveTeamId();
  // Newest openFirst lookup wins: older results are ignored.
  const lookupSeqRef = useRef(0);

  // `?column=in_review|changes_requested|released|scheduled`: scroll to that column and focus its heading
  // (on mobile the columns are stacked, so this is the "tab"). Unknown values are ignored. Handled once per
  // value, the URL is left alone so it can not fight the other deep-link effect.
  const [deepLinkedColumn, setDeepLinkedColumn] = useState<ReleaseBoardColumnKey | null>(null);
  const handledColumnRef = useRef<ReleaseBoardColumnKey | null>(null);
  const columnParam = parseBoardColumnParam(searchParams.get('column'));
  useEffect(() => {
    if (columnParam === null) {
      handledColumnRef.current = null; // a later link to the same column works again
      return;
    }
    if (availability !== 'active' || handledColumnRef.current === columnParam) return;
    const columnEl = document.getElementById(boardColumnDomId(columnParam));
    if (!columnEl) return;
    handledColumnRef.current = columnParam;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    columnEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start', inline: 'nearest' });
    document.getElementById(boardColumnHeadingDomId(columnParam))?.focus({ preventScroll: true });
    setDeepLinkedColumn(columnParam);
  }, [columnParam, availability]);
  useEffect(() => {
    if (deepLinkedColumn === null) return;
    const timer = window.setTimeout(() => setDeepLinkedColumn(null), DEEP_LINK_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [deepLinkedColumn]);

  const boardTitle = t('release_board_title');

  // Create board columns with translations
  const BOARD_COLUMNS = createBoardColumns(t);

  // Deep link `?openFirst=true[&tab=]` (onboarding checklist): open the first In-review item in the global drawer.
  // `?postId=` links are NOT handled here: ReleaseDrawerHost owns that intake (it also works for items that are
  // not on the board, e.g. withdrawn drafts, and for other brands).
  useEffect(() => {
    if (searchParams.get('openFirst') !== 'true') return;
    // Always consume the params (functional form: never write back a stale snapshot of the URL).
    const consumeParams = (): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('openFirst');
          next.delete('tab');
          return next;
        },
        { replace: true }
      );
    };
    if (effectiveTeamId === null) {
      consumeParams();
      return;
    }
    const tabParam = parseDrawerTab(searchParams.get('tab'));
    const lookupSeq = ++lookupSeqRef.current;
    void approvalService
      .getReleaseBoardPage(effectiveTeamId, { ...EMPTY_BOARD_FILTERS, column: 'in_review', limit: 1 })
      .then((page) => {
        if (lookupSeq !== lookupSeqRef.current) return; // a newer lookup superseded this one
        const first = page.items[0];
        if (first) {
          openReleaseDrawer({
            postId: Number(first.post.id),
            variationId: first.variation?.id ?? null,
            tab: tabParam,
            source: 'link',
          });
        }
      })
      .catch(() => {
        /* nothing to open */
      })
      .finally(() => {
        if (lookupSeq !== lookupSeqRef.current) return;
        consumeParams();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, effectiveTeamId]);

  // Auto-complete the onboarding "review a post" item for people who can release
  // (a reviewer role on this board, not an agency/client identity flag).
  const canReleaseAny = board.allItems.some((entry) => entry.canApprove);
  useEffect(() => {
    if (
      progress &&
      !progress.checklistItems?.[CLIENT_CHECKLIST_ITEMS.REVIEW_APPROVAL_POST] &&
      progress.showChecklist &&
      canReleaseAny
    ) {
      completeChecklistItem(CLIENT_CHECKLIST_ITEMS.REVIEW_APPROVAL_POST).catch(() => {
        // Silently fail - non-critical
      });
    }
  }, [progress, completeChecklistItem, canReleaseAny]);

  // DnD sensors - pointer sensor with activation constraint to prevent conflicts with click
  const sensors = useSensors(
    useSensor(InteractiveSafeMouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(InteractiveSafeTouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // Find the active dragged item
  const activeDragItem = useMemo(() => {
    if (!activeId) return null;
    return board.allItems.find((item) => item.id === activeId) || null;
  }, [activeId, board.allItems]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  /**
   * Card nodes are droppables too, so a drop that lands on a card (common on touch,
   * where the finger covers the whole column) reports the card id as `over`.
   * Map it back to the column that card lives in.
   */
  const resolveColumnId = (overId: string): string => {
    if (BOARD_COLUMNS.some((col: BoardColumn) => col.id === overId)) return overId;
    const overItem = board.allItems.find((item) => item.id === overId);
    const overColumn = overItem
      ? BOARD_COLUMNS.find((col: BoardColumn) => col.key === overItem.column)
      : undefined;
    return overColumn ? overColumn.id : overId;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);

    if (!over) return;

    const draggedItemId = active.id as string;
    const targetColumnId = resolveColumnId(over.id as string);

    const draggedItem = board.allItems.find((item) => item.id === draggedItemId);
    if (!draggedItem) return;

    // Find target column
    const targetColumn = BOARD_COLUMNS.find((col: BoardColumn) => col.id === targetColumnId);
    if (!targetColumn) return;

    // Check if drop is allowed on this column
    if (!targetColumn.allowDrop) {
      toast.error(t('approval_cannot_drop', { column: targetColumn.title }));
      return;
    }

    // Already in the target column: nothing to do
    if (draggedItem.column === targetColumn.key) {
      return;
    }

    // Resubmit: Changes Requested → In review (content creator action, not approver)
    if (targetColumn.key === 'in_review') {
      if (draggedItem.column !== 'changes_requested') {
        toast.error(t('approval_resubmit_only_changes'));
        return;
      }
      // Sending for review is a creator action. Anyone who created/sent the item
      // (even if they can also release it) or works as staff may re-send it;
      // reviewer-only people may not.
      const isSender = String(draggedItem.approval.requestedBy?.id) === String(user?.id);
      if (!isSender && !isAgencyStaff && !isInGodMode) {
        toast.error(t('approval_resubmit_not_approver'));
        return;
      }
      setPendingDrop({ item: draggedItem, targetColumn: 'in_review' });
      setShowConfirmDialog(true);
      return;
    }

    // Drop into SCHEDULED column — open schedule modal (released items only)
    if (targetColumn.key === 'scheduled') {
      if (draggedItem.column !== 'released') {
        toast.error(
          t('schedule_approval_only_approved')
        );
        return;
      }
      // Scheduling is an operator action (rulebook `schedule`: creators only). A released item is
      // otherwise schedulable, so the role is the only open question here.
      if (postPermissions.workflowEnabled && !postPermissions.isCreator) {
        toast.error(i18n.t('errors:release.not_creator', { keySeparator: false }));
        return;
      }
      setSchedulePendingItem(draggedItem);
      setShowScheduleModal(true);
      return;
    }

    // Prevent re-processing of already released/scheduled posts for other columns
    if (draggedItem.column === 'released' || draggedItem.column === 'scheduled') {
      toast.error(t('approval_already_processed'));
      return;
    }

    // Release / Request changes require canApprove permission
    if (!draggedItem.canApprove) {
      toast.error(t('approval_only_reviewers_can_release'));
      return;
    }

    if (targetColumn.key === 'changes_requested') {
      // Request changes happens in the Release drawer (pen already active).
      openReleaseDrawer({
        postId: Number(draggedItem.post.id),
        variationId: draggedItem.variation?.id ?? null,
        openInChangesMode: true,
        source: 'board',
      });
    } else if (targetColumn.key === 'released') {
      setPendingDrop({ item: draggedItem, targetColumn: 'released' });
      setShowConfirmDialog(true);
    }
  };

  const handleDragOver = (event: DragEndEvent) => {
    const { over } = event;
    setOverColumnId(over ? resolveColumnId(over.id as string) : null);
  };

  /** Reload every column + the counts (prefix invalidation of the board keys). */
  const refreshBoard = (): void => {
    void queryClient.invalidateQueries({
      queryKey: teamQueryKeys.approval.board.all(effectiveTeamId),
    });
  };

  const handleConfirmAction = async () => {
    if (!pendingDrop) return;

    try {
      if (pendingDrop.targetColumn === 'in_review') {
        // Resubmit for approval — use variation endpoint if this is a variation
        if (pendingDrop.item.variation) {
          await resubmitVariationMutation.mutateAsync({
            postId: Number(pendingDrop.item.post.id),
            variationId: pendingDrop.item.variation.id,
          });
        } else {
          await resubmitMutation.mutateAsync({
            postId: Number(pendingDrop.item.post.id),
          });
        }
        announceReleaseMoment('sent');
      } else {
        // Release
        const result = await approveMutation.mutateAsync({
          approvalId: Number(pendingDrop.item.approval.id),
        });
        // Global "Released · Undo" snackbar for the releaser
        announceRelease({
          postId: Number(pendingDrop.item.post.id),
          variationId: pendingDrop.item.variation?.id ?? null,
          undoUntil: result.undoUntil,
          reviewedAt: result.reviewedAt,
          undoAvailable: result.undoAvailable,
        });
        announceReleaseMoment('released', 1, result.undoAvailable === false);
      }
      setShowConfirmDialog(false);
      setPendingDrop(null);
      refreshBoard();
    } catch (err) {
      const message = formatApiError(err).message || t('release_failed');
      toast.error(message);
    }
  };

  const handleCardClick = (item: PendingApproval) => {
    // Only open the drawer if not currently dragging
    if (!activeId) {
      openReleaseDrawer({
        postId: Number(item.post.id),
        variationId: item.variation?.id ?? null,
        source: 'board',
      });
    }
  };

  // Refresh the board when the drawer closes (belt and braces: mutations already invalidate release state).
  const drawerOpen = useReleaseDrawerStore((state) => state.target !== null);
  const wasDrawerOpenRef = useRef(false);
  useEffect(() => {
    if (wasDrawerOpenRef.current && !drawerOpen) refreshBoard();
    wasDrawerOpenRef.current = drawerOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title={
            <>
              <span className="text-primary-500">{boardTitle.charAt(0)}</span>
              {boardTitle.slice(1)}
            </>
          }
          subtitle={t('approval_board_subtitle')}
          icon={ICONS.CHECK_CIRCLE}
          headerType="approval"
          documentTitle={boardTitle}
        />

        {availability === 'loading' && (
          <>
            <BoardFilterBarSkeleton />
            <BoardSkeleton columns={BOARD_COLUMNS} />
          </>
        )}

        {availability !== 'active' && availability !== 'loading' && (
          <BoardUnavailable availability={availability} canOpenPortfolio={isAgencyStaff} />
        )}

        {availability === 'active' && (
          <BoardFilterBar
            filters={filters}
            activeCount={activeCount}
            onChange={setFilters}
            onClearAll={clearAll}
          />
        )}

        {/* Board */}
        {availability === 'active' && (
        <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 lg:overflow-x-auto pb-4 lg:min-h-[600px]">
          {BOARD_COLUMNS.map((column: BoardColumn) => (
            <BoardColumnComponent
              key={column.id}
              column={column}
              state={board.columns[column.key]}
              total={board.counts ? board.counts[column.key] : null}
              filtersActive={activeCount > 0}
              onCardClick={handleCardClick}
              onWithdraw={setWithdrawTarget}
              isOver={overColumnId === column.id}
              isDeepLinked={deepLinkedColumn === column.key}
            />
          ))}
        </div>
        )}

        {/* Empty state hint */}
        {availability === 'active' &&
          activeCount === 0 &&
          !!board.counts &&
          board.counts.in_review + board.counts.changes_requested + board.counts.released + board.counts.scheduled === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 mb-4">
              <Icon name={ICONS.CHECK_CIRCLE} size="xl" color="text-emerald-500" ariaHidden />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('approval_all_caught_up')}</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              {t('approval_all_caught_up_desc')}
            </p>
            <Button variant="primary" onClick={() => navigate('/creator')}>
              <Icon name={ICONS.PEN} size="sm" className="mr-2" ariaHidden />
              {t('approval_create_new_post')}
            </Button>
          </div>
        )}

        {/* First-time reviewer tour (once per person) */}
        <ReleaseTour drawerOpen={drawerOpen} />

        {/* Approval confirmation dialog */}
        <ConfirmDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          title={pendingDrop?.targetColumn === 'in_review'
            ? t('release_resend_title')
            : t('release_cta_release')}
          description={pendingDrop?.targetColumn === 'in_review'
            ? t('release_resend_desc')
            : t('release_confirm_desc')}
          confirmText={pendingDrop?.targetColumn === 'in_review'
            ? t('release_resend_action')
            : t('release_cta_release')}
          cancelText={t('approval_cancel')}
          variant="default"
          onConfirm={handleConfirmAction}
          onCancel={() => setPendingDrop(null)}
          loading={pendingDrop?.targetColumn === 'in_review' ? (resubmitMutation.isPending || resubmitVariationMutation.isPending) : approveMutation.isPending}
        />

        {/* Withdraw from review (card menu): confirm first */}
        <ConfirmDialog
          open={withdrawTarget !== null}
          onOpenChange={(open) => !open && setWithdrawTarget(null)}
          title={t('release_withdraw_confirm_title')}
          description={t('release_withdraw_confirm_desc')}
          confirmText={t('release_withdraw_confirm_action')}
          cancelText={t('approval_cancel')}
          variant="warning"
          onConfirm={async () => {
            const target = withdrawTarget;
            if (!target) return;
            const withdrawn = await withdrawAction.withdraw({ approvalId: Number(target.approval.id), postId: Number(target.post.id) });
            if (withdrawn) setWithdrawTarget(null);
          }}
          onCancel={() => setWithdrawTarget(null)}
          loading={withdrawAction.isPending}
          closeOnConfirm={false}
        />

        {/* Schedule from approval board modal */}
        {schedulePendingItem && (
          <ScheduleFromApprovalModal
            item={schedulePendingItem}
            isOpen={showScheduleModal}
            onClose={() => {
              setShowScheduleModal(false);
              setSchedulePendingItem(null);
            }}
            onSuccess={() => {
              setShowScheduleModal(false);
              setSchedulePendingItem(null);
              toast.success(t('schedule_approval_success'));
              refreshBoard();
            }}
          />
        )}

        {/* Drag overlay - shows dragged card */}
        <DragOverlay>
          {activeDragItem ? (
            <div className="rotate-3 scale-105 w-32">
              <ApprovalCard item={activeDragItem} onClick={() => {}} isDragging />
            </div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default ApprovalBoard;
